import React, { useState, useRef } from "react";
import { X, Palette } from "lucide-react";
import { SketchPicker } from 'react-color';
import { 
  TIME_SIGNATURE_COLORS, 
  COMMON_TIME_SIGNATURES,
  DEFAULT_TIME_SIGNATURE_COLORS 
} from "../data/musicData";

const TimeSignatureColorsModal = ({ 
  isOpen, 
  onClose, 
  timeSignatureColors,
  setTimeSignatureColors 
}) => {
  const [localColors, setLocalColors] = useState({ ...timeSignatureColors });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeSignature, setActiveSignature] = useState(null);
  const [pickerColor, setPickerColor] = useState('#ffffff');
  const [customNumerator, setCustomNumerator] = useState("");
  const [customDenominator, setCustomDenominator] = useState("4");
  
  const modalRef = useRef(null);

  if (!isOpen) return null;

  // Convert Tailwind class to hex for the color picker
  const tailwindToHex = (colorValue) => {
    // If it's already a hex color, return it
    if (colorValue.startsWith('#')) {
      return colorValue;
    }
    
    // Otherwise, look up the Tailwind class
    const colorEntry = TIME_SIGNATURE_COLORS.find(color => color.value === colorValue);
    return colorEntry ? colorEntry.hex : '#ffffff';
  };

  // Get color style for preview (handles both Tailwind classes and hex colors)
  const getColorStyle = (colorValue) => {
    if (colorValue.startsWith('#')) {
      // Custom hex color - use inline style
      return { backgroundColor: colorValue };
    }
    
    // Tailwind class - find the hex value for consistent preview
    const colorEntry = TIME_SIGNATURE_COLORS.find(color => color.value === colorValue);
    return colorEntry ? { backgroundColor: colorEntry.hex } : { backgroundColor: '#ffffff' };
  };

  // Get CSS class for preview (only for Tailwind colors)
  const getColorClass = (colorValue) => {
    return colorValue.startsWith('#') ? '' : colorValue;
  };

  const handleColorSelect = (timeSignature, colorValue) => {
    setLocalColors(prev => ({
      ...prev,
      [timeSignature]: colorValue
    }));
  };

  const openColorPicker = (signature, e) => {
    e.stopPropagation();
    const currentColor = tailwindToHex(localColors[signature]);
    setPickerColor(currentColor);
    setActiveSignature(signature);
    setShowColorPicker(true);
  };

  const closeColorPicker = () => {
    setShowColorPicker(false);
    setActiveSignature(null);
  };

  const handleColorPickerChange = (color) => {
    // Update the picker's internal color state immediately for visual feedback
    setPickerColor(color.hex);
    
    // Store the exact hex color (no matching to Tailwind colors)
    handleColorSelect(activeSignature, color.hex);
  };

  const addCustomTimeSignature = () => {
    const num = parseInt(customNumerator);
    const den = parseInt(customDenominator);
    
    if (num > 0 && den > 0 && [1, 2, 4, 8, 16, 32].includes(den)) {
      const signature = `${num}/${den}`;
      setLocalColors(prev => ({
        ...prev,
        [signature]: "bg-white"
      }));
      setCustomNumerator("");
      setCustomDenominator("4");
    }
  };

  const removeCustomTimeSignature = (signature) => {
    setLocalColors(prev => {
      const newColors = { ...prev };
      delete newColors[signature];
      return newColors;
    });
  };

  const handleSave = () => {
    setTimeSignatureColors(localColors);
    onClose();
  };

  const handleReset = () => {
    setLocalColors({ ...DEFAULT_TIME_SIGNATURE_COLORS });
  };

  // Get all time signatures
  const allTimeSignatures = Object.keys(localColors).sort((a, b) => {
    const [aNum, aDen] = a.split('/').map(Number);
    const [bNum, bDen] = b.split('/').map(Number);
    
    if (aDen !== bDen) return aDen - bDen;
    return aNum - bNum;
  });

  const isCommonSignature = (signature) => {
    return COMMON_TIME_SIGNATURES.some(ts => `${ts.numerator}/${ts.denominator}` === signature);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div ref={modalRef} className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">Time Signature Colors</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Assign colors to different time signatures. Use preset colors or pick any custom color.
          </p>

          {/* Time Signatures */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Time Signatures</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {allTimeSignatures.map((signature) => (
                <div key={signature} className="border rounded-lg p-3 relative">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">{signature}</span>
                    {!isCommonSignature(signature) && (
                      <button
                        onClick={() => removeCustomTimeSignature(signature)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Preview */}
                  <div 
                    className={`w-full h-8 rounded border-2 border-gray-300 mb-2 ${getColorClass(localColors[signature])}`}
                    style={getColorStyle(localColors[signature])}
                  />

                  {/* Color Selection */}
                  <div className="grid grid-cols-8 gap-1 mb-2">
                    {TIME_SIGNATURE_COLORS.slice(0, 16).map((color) => (
                      <button
                        key={color.value}
                        onClick={() => handleColorSelect(signature, color.value)}
                        className={`w-4 h-4 rounded border ${color.value} ${
                          localColors[signature] === color.value 
                            ? 'border-2 border-black' 
                            : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      />
                    ))}
                  </div>

                  {/* Color Picker Button */}
                  <div className="text-center">
                    <button
                      onClick={(e) => openColorPicker(signature, e)}
                      className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded border"
                      title="Open color picker"
                    >
                      <Palette className="w-3 h-3 mr-1" />
                      Custom Color
                    </button>
                  </div>

                  {/* Color Picker Popup */}
                  {showColorPicker && activeSignature === signature && (
                    <div className="absolute top-full left-0 right-0 mt-2 z-30">
                      {/* Backdrop to close picker */}
                      <div 
                        className="fixed inset-0 -z-10" 
                        onClick={closeColorPicker}
                      />
                      <div className="relative bg-white rounded-lg shadow-xl border-2 border-gray-200 p-2 mx-auto w-fit">
                        <SketchPicker
                          color={pickerColor}
                          onChange={handleColorPickerChange}
                          disableAlpha={true}
                          presetColors={TIME_SIGNATURE_COLORS.map(color => color.hex)}
                        />
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          Exact color will be used
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Add Custom Time Signature */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold mb-3">Add Custom Time Signature</h3>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                max="32"
                value={customNumerator}
                onChange={(e) => setCustomNumerator(e.target.value)}
                placeholder="#Beats"
                className="w-24 p-2 border rounded"
              />
              <span className="text-xl">/</span>
              <select
                value={customDenominator}
                onChange={(e) => setCustomDenominator(e.target.value)}
                className="p-2 border rounded"
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="4">4</option>
                <option value="8">8</option>
                <option value="16">16</option>
                <option value="32">32</option>
              </select>
              <button
                onClick={addCustomTimeSignature}
                disabled={!customNumerator || parseInt(customNumerator) <= 0}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300"
              >
                Add
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between">
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Reset to Default
            </button>
            <div className="space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSignatureColorsModal;